import { fetchSessionData } from './api_session';
import {
    CognitoUserPool,
    CognitoUser,
    AuthenticationDetails,
    CognitoUserAttribute
} from 'amazon-cognito-identity-js';

// senior3d1f9118_app_clientWeb
const poolData = {
    UserPoolId: 'us-east-2_ZInLl3eoa',
    ClientId: '11lkfffobu098ncuvvudipsm71',
};

const userPool = new CognitoUserPool(poolData);

export const changeUserPassword = (oldPassword, newPassword) => {
    return new Promise((resolve, reject) => {
        const currentUser = userPool.getCurrentUser();
        if (!currentUser) { reject(new Error('No current user')); return; }

        currentUser.getSession((err, session) => {
            if (err || !session?.isValid()) { reject(err || new Error('Invalid session')); return; }

            currentUser.changePassword(oldPassword, newPassword, (e, result) => {
                if (e) { reject(e); } else { resolve(result); }
            });
        });
    });
};

export const updateUserEmail = (newEmail) => {
    return new Promise((resolve, reject) => {
        const currentUser = userPool.getCurrentUser();
        if (!currentUser) { reject(new Error('No current user')); return; }

        const attr = new CognitoUserAttribute({ Name: 'email', Value: newEmail });
        currentUser.updateAttributes([attr], (err, result) => {
            if (err) { reject(err); } else { resolve(result); }
        });
    });
};

export const forgotPassword = (username) => {
    return new Promise((resolve, reject) => {
        const user = new CognitoUser({ Username: username, Pool: userPool });
        user.forgotPassword({
            onSuccess: (data) => resolve(data),   // {CodeDeliveryDetails}
            onFailure: (err)   => reject(err),
        });
    });
};

export const confirmForgotPassword = (username, verificationCode, newPassword) => {
    return new Promise((resolve, reject) => {
        const user = new CognitoUser({ Username: username, Pool: userPool });
        user.confirmPassword(verificationCode, newPassword, {
            onSuccess: ()   => resolve(),
            onFailure: (e)  => reject(e),
        });
    });
};

export const signIn = async (username, password) => {
    const user = new CognitoUser({ Username: username, Pool: userPool });
    const authDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
    });

    return new Promise((resolve, reject) => {
        user.authenticateUser(authDetails, {
            onSuccess: (session) => {
                // Store the required tokens and user information in local storage
                localStorage.setItem('accessToken', session.getAccessToken().getJwtToken());
                localStorage.setItem('idToken', session.getIdToken().getJwtToken());
                localStorage.setItem('refreshToken', session.getRefreshToken().getToken());
                localStorage.setItem('LastAuthUser', username);
                localStorage.setItem("session", JSON.stringify(session));

                resolve(session);
            },
            onFailure: (err) => reject(err),
            newPasswordRequired: (userAttributes, requiredAttributes) => {
                // userAttributes contains the current user attributes
                // requiredAttributes contains which attributes are required to be set
                resolve({ newPasswordRequired: true, user, session: requiredAttributes, userAttributes });
            },
        });
    });
};

export const signUp = async (username, password, email, name, phone_number) => {
    return new Promise((resolve, reject) => {
        userPool.signUp(username, password, [
            { Name: 'email', Value: email },
            { Name: 'name', Value: name },
            { Name: 'phone_number', Value: phone_number },
            // Additional attributes can be added here
        ], null, (err, result) => {
            if (err) {
                reject(err);
                return;
            }
            resolve(result.user);
        });
    });
};


export const signOut = () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser != null) {
        currentUser.signOut();
    }    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('LastAuthUser');
    localStorage.removeItem('session');
};

export const getNameOfUser = () => {
    try {
        const session = JSON.parse(localStorage.getItem('session'));
        return session.idToken.payload.name;
    } catch (error) {
        console.error(error);
        return "";
    }
};

export const getEmailOfUser = () => {
    try {
        const session = JSON.parse(localStorage.getItem('session'));
        return session.idToken.payload.email;
    } catch (error) {
        console.error(error);
        return "";
    }
};

export const getPhoneNumberOfUser = () => {
    try {
        const session = JSON.parse(localStorage.getItem('session'));
        return session.idToken.payload.phone_number;
    } catch (error) {
        console.error(error);
        return "";
    }
};

export const getSubOfUser = () => {
    try {
        const session = JSON.parse(localStorage.getItem('session'));
        return session.idToken.payload.sub;
    } catch (error) {
        console.error(error);
        return "";
    }
};

export const getIsAuthenticated = () => {
    return localStorage.getItem('accessToken') !== null;
}

export const checkForTokenExpirationAndRefresh = async () => {
    if (shouldRefreshTokens()) {
        try {
            await refreshTokens();
        } catch (error) {
            console.error(error);
        }
    }
}

export const shouldRefreshTokens = () => {
    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
        console.log('No ID token found in localStorage. Token should be refreshed.');
        return true;
    }

    const decodedToken = JSON.parse(atob(idToken.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = decodedToken.exp - currentTime; // Time until expiry in seconds

    console.log(`Current Time: ${new Date(currentTime * 1000).toISOString()}`);
    console.log(`Token Expiry Time: ${new Date(decodedToken.exp * 1000).toISOString()}`);
    console.log(`Time Until Expiry: ${timeUntilExpiry} seconds`);

    // Refresh if within 10 minutes (600 seconds) of expiry
    const shouldRefresh = currentTime > decodedToken.exp - 600;
    console.log(`Should the token be refreshed? ${shouldRefresh}`);

    return shouldRefresh;
};

export const getUserGroups = () => {
    return new Promise((resolve, reject) => {
        const currentUser = userPool.getCurrentUser();
        if (currentUser) {
            currentUser.getSession((err, session) => {
                if (err) {
                    reject(err);
                    return;
                }

                // Use the ID token to get user attributes
                const idToken = session.getIdToken().getJwtToken();
                const decodedToken = JSON.parse(atob(idToken.split('.')[1]));

                // The 'cognito:groups' claim contains the user's groups
                const groups = decodedToken['cognito:groups'] || [];
                resolve(groups);
            });
        } else {
            reject(new Error("No current user"));
        }
    });
};

const processRefreshedToken = (session) => {
    // Extract Cognito session details and set user context
    const { accessToken /*, idToken, refreshToken*/ } = session;
    //const payload = idToken.payload;

    fetchSessionData(accessToken).then((sessionData) => {
        if (sessionData) {
        console.error('Error fetching session data');
        }});
};
 

export const refreshTokens = () => {
    return new Promise((resolve, reject) => {
        const currentUser = userPool.getCurrentUser();

        if (currentUser != null) {
            currentUser.getSession((err, session) => {
                if (err) {
                    reject(err);
                    return;
                }

                /*if (session.isValid()) {
                    resolve(session);
                    return;
                }*/

                const refreshToken = session.getRefreshToken();
                currentUser.refreshSession(refreshToken, (refreshErr, newSession) => {
                    console.log("Session has been refreshed");
                    if (refreshErr) {
                        reject(refreshErr);
                    } else {
                        // Update the tokens and session in local storage
                        localStorage.setItem('accessToken', newSession.getAccessToken().getJwtToken());
                        localStorage.setItem('idToken', newSession.getIdToken().getJwtToken());
                        localStorage.setItem('refreshToken', newSession.getRefreshToken().getToken());
                        localStorage.setItem("session", JSON.stringify(newSession));
                        processRefreshedToken(newSession);
                        resolve(newSession);
                    }
                });
            });
        } else {
            reject(new Error("No current user"));
        }
    });
};

export const completeNewPassword = (user, newPassword, session, additionalAttributes) => {
    return new Promise((resolve, reject) => {
        user.completeNewPasswordChallenge(newPassword, additionalAttributes, {
            onSuccess: (newSession) => {
                // Store the required tokens and user information in local storage
                localStorage.setItem('accessToken', newSession.getAccessToken().getJwtToken());
                localStorage.setItem('idToken', newSession.getIdToken().getJwtToken());
                localStorage.setItem('refreshToken', newSession.getRefreshToken().getToken());
                localStorage.setItem('LastAuthUser', user.getUsername());
                localStorage.setItem("session", JSON.stringify(newSession));

                resolve(newSession);
            },
            onFailure: (err) => reject(err),
            mfaRequired: (codeDeliveryDetails) => {
                // print to console
                console.log('MFA is required: ' + codeDeliveryDetails);
                // Handle MFA scenario if needed
                // This part depends on how your application handles MFA
            },
            customChallenge: (challengeParameters) => {
                console.log('Custom challenge is required: ' + challengeParameters);
                // Handle custom challenge if needed
                // This part depends on your specific custom challenge requirements
            }
        });
    });
};